import { Color } from "@repo/app-types/constants/colors";
import {
  computedFieldDefinitionIdSchema,
  type ComputedFieldDefinition,
  type ComputedFieldDefinitionId,
} from "@repo/app-types/entities/computedField";
import {
  buildOutcomeGreenRateLabel,
  getOutcomeNoun,
  getOutcomePluralNoun,
  isRevenueOutcomeDefinition,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import type {
  PlaybookSectionId,
  PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import type { TeammateSectionScore } from "@repo/app-types/entities/teammatePerformanceSummary";
import { formatCallLengthHoursMinutes } from "@repo/app-types/helpers/formatCallLength";
import { isNotNullNotUndefined } from "@repo/app-types/helpers/utilities";
import { capitalize, Size } from "@repo/ui/helpers/helpers";
import { Pill } from "@repo/ui/Pills/pill";
import type { EntityTableColumnDefinition } from "@repo/ui/tables/entityTable";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { ReactNode } from "react";
import type { Key } from "react-aria-components";
import { customerExperienceScoreToPercent } from "../topics/topicStatsFormatters.js";
import { CustomerExperienceScorePercentPill } from "./customerExperienceScorePercentPill.js";
import { renderGapBarWithTooltip } from "./gapBar.js";
import {
  computePassRatePillColor,
  GradingPassRatePill,
} from "./gradingPassRatePill.js";
import { GradingTalkRatioPill } from "./gradingTalkRatioPill.js";
import { OutcomeRatePill } from "./outcomeRatePill.js";
import { PercentagePill } from "./percentagePill.js";

export function headerWithTooltip(
  label: ReactNode,
  tooltip: ReactNode,
): ReactNode {
  return (
    <TooltipTrigger>
      <TooltipHoverTarget>{label}</TooltipHoverTarget>
      <SimpleTooltip>{tooltip}</SimpleTooltip>
    </TooltipTrigger>
  );
}

/**
 * Shared column-id constants for the performance-metric columns the
 * by-teammate (`TeammatesTable`) and by-group (`GroupsTable`) tables both
 * render.
 */
export const PerformanceColumnId = {
  PlaybookScore: "playbookScore",
  PlaybookSectionScorePrefix: "playbookSectionScore",
  ComputedFieldPrefix: "computedField",
  OutcomeScore: "outcomeScore",
  OutcomeCount: "outcomeCount",
  CustomerExperienceScore: "customerExperienceScore",
  SecondsPerGreenOutcome: "secondsPerGreenOutcome",
  AvgRevenue: "avgRevenue",
  PremiumPerHour: "premiumPerHour",
  KnowledgeBaseAdherenceScore: "knowledgeBaseAdherenceScore",
  TalkRatio: "talkRatio",
  Calls: "calls",
  /** Coaching goal progress — by-teammate table only (groups have no goal). */
  CoachingGoal: "coachingGoal",
} as const;

/** Build the column id used for a computed field column. */
export function buildComputedFieldColumnId(computedFieldId: string): string {
  return `${PerformanceColumnId.ComputedFieldPrefix}:${computedFieldId}`;
}

/**
 * Pull the computed field id out of a column id, if it's a computed-field
 * column whose suffix parses as a valid UUID. Returns `undefined` for
 * unrelated column ids or malformed / stale persisted entries — column
 * ids come from saved UI preferences, so a throwing parser would crash
 * the page on stale data.
 */
export function readComputedFieldIdFromColumnId(
  columnId: string,
): ComputedFieldDefinitionId | undefined {
  const prefix = `${PerformanceColumnId.ComputedFieldPrefix}:`;
  if (!columnId.startsWith(prefix)) return undefined;
  const parsed = computedFieldDefinitionIdSchema.safeParse(
    columnId.slice(prefix.length),
  );
  return parsed.success ? parsed.data : undefined;
}

export const togglablePerformanceMetricColumnIds: readonly string[] = [
  PerformanceColumnId.Calls,
  PerformanceColumnId.CoachingGoal,
  PerformanceColumnId.OutcomeScore,
  PerformanceColumnId.OutcomeCount,
  PerformanceColumnId.CustomerExperienceScore,
  PerformanceColumnId.SecondsPerGreenOutcome,
  PerformanceColumnId.AvgRevenue,
  PerformanceColumnId.PremiumPerHour,
  PerformanceColumnId.KnowledgeBaseAdherenceScore,
  PerformanceColumnId.TalkRatio,
];

export const togglablePerformanceMetricColumnIdSet: ReadonlySet<string> =
  new Set<string>(togglablePerformanceMetricColumnIds);

/**
 * True if `columnId` is a toggleable metric column. Returns true for both
 * the static enum values in `togglablePerformanceMetricColumnIdSet` and
 * any dynamic computed-field column (prefixed `computedField:`).
 */
export function isTogglableMetricColumnId(columnId: string): boolean {
  if (togglablePerformanceMetricColumnIdSet.has(columnId)) return true;
  return readComputedFieldIdFromColumnId(columnId) !== undefined;
}

const performanceMetricColumnOrder: readonly string[] = [
  PerformanceColumnId.OutcomeScore,
  PerformanceColumnId.OutcomeCount,
  PerformanceColumnId.Calls,
  PerformanceColumnId.SecondsPerGreenOutcome,
  PerformanceColumnId.AvgRevenue,
  PerformanceColumnId.PremiumPerHour,
  PerformanceColumnId.CustomerExperienceScore,
  PerformanceColumnId.KnowledgeBaseAdherenceScore,
  PerformanceColumnId.TalkRatio,
  PerformanceColumnId.ComputedFieldPrefix,
  PerformanceColumnId.CoachingGoal,
];

/** Keeps the Team tables and their column menus in the same display order. */
export function sortPerformanceColumns<T extends { id: string }>(
  columns: readonly T[],
): T[] {
  const rankColumn = (column: T) => {
    const id = readComputedFieldIdFromColumnId(column.id)
      ? PerformanceColumnId.ComputedFieldPrefix
      : column.id;
    // Identity and playbook columns stay first, in their original order.
    return performanceMetricColumnOrder.indexOf(id);
  };
  return [...columns].sort(
    (left, right) => rankColumn(left) - rankColumn(right),
  );
}

export interface PerformanceMetricColumnOptionsInput {
  outcomeDefinition: OutcomeDefinition | undefined;
  shouldShowKnowledgeBaseAdherenceColumn: boolean;
  shouldShowCustomerExperienceScoreColumn: boolean;
  /**
   * Whether the coaching-goal progress column is eligible. Only the
   * by-teammate table sets this — a group has no per-teammate goal.
   */
  includesCoachingGoalColumn?: boolean;
  computedFields?: readonly ComputedFieldDefinition[];
}

export interface PerformanceMetricColumnOption {
  id: string;
  label: string;
}

export function buildPerformanceMetricColumnOptions({
  outcomeDefinition,
  shouldShowKnowledgeBaseAdherenceColumn,
  shouldShowCustomerExperienceScoreColumn,
  includesCoachingGoalColumn,
  computedFields,
}: PerformanceMetricColumnOptionsInput): readonly PerformanceMetricColumnOption[] {
  const options: PerformanceMetricColumnOption[] = [];

  if (outcomeDefinition) {
    options.push({
      id: PerformanceColumnId.OutcomeScore,
      label: buildOutcomeGreenRateLabel(outcomeDefinition),
    });
    options.push({
      id: PerformanceColumnId.OutcomeCount,
      label: capitalize(getOutcomePluralNoun(outcomeDefinition)),
    });
  }

  if (shouldShowCustomerExperienceScoreColumn) {
    options.push({
      id: PerformanceColumnId.CustomerExperienceScore,
      label: "CX Score",
    });
  }

  if (outcomeDefinition) {
    const outcomeNoun = getOutcomeNoun(outcomeDefinition);
    if (!isRevenueOutcomeDefinition(outcomeDefinition)) {
      options.push({
        id: PerformanceColumnId.SecondsPerGreenOutcome,
        label: `Avg time to ${outcomeNoun}`,
      });
    }
    if (isRevenueOutcomeDefinition(outcomeDefinition)) {
      options.push({
        id: PerformanceColumnId.AvgRevenue,
        label: "Premium / Opp",
      });
      options.push({
        id: PerformanceColumnId.PremiumPerHour,
        label: "Premium / hr",
      });
    }
  }

  if (shouldShowKnowledgeBaseAdherenceColumn) {
    options.push({
      id: PerformanceColumnId.KnowledgeBaseAdherenceScore,
      label: "KB",
    });
  }

  options.push({
    id: PerformanceColumnId.TalkRatio,
    label: "Talk ratio",
  });

  for (const computedField of computedFields ?? []) {
    options.push({
      id: buildComputedFieldColumnId(computedField.id),
      label: computedField.display_name,
    });
  }

  if (includesCoachingGoalColumn) {
    options.push({
      id: PerformanceColumnId.CoachingGoal,
      label: "Goal progress",
    });
  }
  options.push({ id: PerformanceColumnId.Calls, label: "Calls" });

  return sortPerformanceColumns(options);
}

/**
 * Sort fields the shared metric columns cover. Both
 * `GradingsByTeammateSortField` and `GradingsByGroupSortField` are
 * supersets of this union.
 */
export type PerformanceSortField =
  | "playbook_score"
  | `playbook_section_score:${string}`
  // Criterion sorting is exposed by the ClickHouse layer (used by the
  // list-teammates MCP tool) but has no column in these admin tables, so it
  // maps to "no column" (undefined) in performanceSortFieldToColumnId.
  | `playbook_criterion_score:${string}`
  | `computed_field:${string}`
  | "outcome_green_rate"
  | "outcome_green_count"
  | "customer_experience_score"
  | "seconds_per_green_outcome"
  | "outcome_average_revenue"
  | "revenue_per_hour"
  | "knowledge_base_adherence_correct_call_rate"
  | "talk_ratio";

export function performanceColumnIdToSortField(
  columnId: Key | undefined,
): PerformanceSortField | undefined {
  switch (columnId) {
    case PerformanceColumnId.PlaybookScore:
      return "playbook_score";

    case PerformanceColumnId.OutcomeScore:
      return "outcome_green_rate";

    case PerformanceColumnId.OutcomeCount:
      return "outcome_green_count";

    case PerformanceColumnId.CustomerExperienceScore:
      return "customer_experience_score";

    case PerformanceColumnId.SecondsPerGreenOutcome:
      return "seconds_per_green_outcome";

    case PerformanceColumnId.AvgRevenue:
      return "outcome_average_revenue";

    case PerformanceColumnId.PremiumPerHour:
      return "revenue_per_hour";

    case PerformanceColumnId.KnowledgeBaseAdherenceScore:
      return "knowledge_base_adherence_correct_call_rate";

    case PerformanceColumnId.TalkRatio:
      return "talk_ratio";
  }

  if (
    typeof columnId === "string" &&
    columnId.startsWith(`${PerformanceColumnId.PlaybookSectionScorePrefix}:`)
  ) {
    return `playbook_section_score:${columnId.slice(PerformanceColumnId.PlaybookSectionScorePrefix.length + 1)}`;
  }

  if (typeof columnId === "string") {
    const computedFieldId = readComputedFieldIdFromColumnId(columnId);
    if (computedFieldId !== undefined) {
      return `computed_field:${computedFieldId}`;
    }
  }

  return undefined;
}

export function performanceSortFieldToColumnId(
  sortField: PerformanceSortField | undefined,
): string | undefined {
  switch (sortField) {
    case "playbook_score":
      return PerformanceColumnId.PlaybookScore;

    case "outcome_green_rate":
      return PerformanceColumnId.OutcomeScore;

    case "outcome_green_count":
      return PerformanceColumnId.OutcomeCount;

    case "customer_experience_score":
      return PerformanceColumnId.CustomerExperienceScore;

    case "seconds_per_green_outcome":
      return PerformanceColumnId.SecondsPerGreenOutcome;

    case "outcome_average_revenue":
      return PerformanceColumnId.AvgRevenue;

    case "revenue_per_hour":
      return PerformanceColumnId.PremiumPerHour;

    case "knowledge_base_adherence_correct_call_rate":
      return PerformanceColumnId.KnowledgeBaseAdherenceScore;

    case "talk_ratio":
      return PerformanceColumnId.TalkRatio;
  }

  if (sortField?.startsWith("playbook_section_score:")) {
    return `${PerformanceColumnId.PlaybookSectionScorePrefix}:${sortField.slice("playbook_section_score:".length)}`;
  }

  if (sortField?.startsWith("computed_field:")) {
    return buildComputedFieldColumnId(
      sortField.slice("computed_field:".length),
    );
  }

  return undefined;
}

/**
 * Minimal row shape the shared metric columns read from. Both
 * `VisualTeamPerformancePageRow` and `ResolvedTeamPerformanceByGroupRow`
 * extend / structurally satisfy this.
 */
export interface PerformanceRow {
  id: string;
  scores: {
    pass_rate: number | undefined;
    talk_ratio: number | undefined;
    outcome_green_rate?: number;
    outcome_green_count?: number;
    incorrect_call_ratio?: number;
    average_revenue?: number;
    revenue_per_hour?: number;
    seconds_per_green_outcome?: number;
    customer_experience_score?: number;
  };
  section_scores?: Record<PlaybookSectionId, TeammateSectionScore>;
  computed_field_values?: Record<ComputedFieldDefinitionId, number | null>;
}

/**
 * Cohort medians / benchmarks the gap bars compare each row against.
 * Populated by the by-teammate / by-group endpoints from the same `OVER all`
 * window functions, so both rollups feed identically-shaped values in.
 */
export interface PerformanceBenchmarks {
  medianPassRate?: number;
  medianTalkRatio?: number;
  medianOutcomeGreenRate?: number;
  medianOutcomeGreenCount?: number;
  averageRevenueBenchmark?: number;
  medianRevenuePerHour?: number;
  medianSecondsPerGreenOutcome?: number;
  medianSectionScores?: Record<string, number>;
  medianCustomerExperienceScore?: number;
  /** Per-computed-field median, keyed by computed field id. */
  medianComputedFieldValues?: Record<ComputedFieldDefinitionId, number | null>;
}

interface BuildPerformanceColumnsOptions {
  benchmarks: PerformanceBenchmarks;
  outcomeDefinition: OutcomeDefinition | undefined;
  shouldShowKnowledgeBaseAdherenceColumn: boolean;
  shouldShowCustomerExperienceScoreColumn: boolean;
  showsPlaybookColumn: boolean;
  selectedSections: readonly PlaybookSectionNode[];
  /**
   * Computed field definitions to render as dynamic toggleable columns.
   * Treated identically to other toggleable metric columns — gated by
   * `selectedMetricColumnIds`.
   */
  computedFields?: readonly ComputedFieldDefinition[];
  /**
   * Set of toggleable metric column IDs the user has chosen to display.
   * Toggleable metric columns (see `togglablePerformanceMetricColumnIds`)
   * whose IDs are absent from this set are hidden. Non-toggleable columns
   * (e.g. the playbook column, section columns) are unaffected.
   */
  selectedMetricColumnIds: ReadonlySet<string>;
}

/**
 * Builds the metric columns shared between `TeammatesTable` and
 * `GroupsTable`. Generic over the row type so each table can pass its own
 * row shape — the columns only read fields declared in
 * `PerformanceRow`.
 */
export function buildPerformanceColumns<R extends PerformanceRow>({
  benchmarks,
  outcomeDefinition,
  shouldShowKnowledgeBaseAdherenceColumn,
  shouldShowCustomerExperienceScoreColumn,
  showsPlaybookColumn,
  selectedSections,
  computedFields,
  selectedMetricColumnIds,
}: BuildPerformanceColumnsOptions): EntityTableColumnDefinition<R>[] {
  const allowsSorting = true;
  const columns: EntityTableColumnDefinition<R>[] = [];

  if (showsPlaybookColumn) {
    columns.push({
      id: PerformanceColumnId.PlaybookScore,
      // Distribute remaining space to the other columns.
      className: "w-0",
      allowsSorting,
      header: headerWithTooltip(
        "Playbook",
        "How closely they follow your playbook on calls.",
      ),
      renderCell: (row) => {
        const score = row.scores.pass_rate;

        if (score === undefined) {
          return <span className="text-muted-foreground">--</span>;
        }

        const scoreValue = Math.round(score * 100);
        const benchmarkValue =
          benchmarks.medianPassRate &&
          Math.round(benchmarks.medianPassRate * 100);

        return (
          <div className="flex items-center gap-2">
            <GradingPassRatePill size={Size.SMALL} passRate={score} />
            {isNotNullNotUndefined(benchmarkValue)
              ? renderGapBarWithTooltip(
                  `${scoreValue}% playbook adherence`,
                  scoreValue,
                  benchmarkValue,
                )
              : null}
          </div>
        );
      },
    });
  }

  // Add a column for each selected section
  for (const section of selectedSections) {
    columns.push({
      id: `${PerformanceColumnId.PlaybookSectionScorePrefix}:${section.id}`,
      className: "w-0",
      allowsSorting,
      header: (
        <TooltipTrigger>
          <TooltipHoverTarget className="block max-w-[100px] truncate">
            {section.name}
          </TooltipHoverTarget>
          <SimpleTooltip>
            How well they follow the {section.name} part of your playbook.
          </SimpleTooltip>
        </TooltipTrigger>
      ),
      renderCell: (row) => {
        const sectionScore = row.section_scores?.[section.id];

        if (sectionScore?.averageScore === undefined) {
          return <span className="text-muted-foreground">--</span>;
        }

        const scoreValue = Math.round(sectionScore.averageScore * 100);
        const medianScore = benchmarks.medianSectionScores?.[section.id];
        const benchmarkValue =
          medianScore !== undefined ? Math.round(medianScore * 100) : undefined;

        return (
          <div className="flex items-center gap-2">
            <GradingPassRatePill
              size={Size.SMALL}
              passRate={sectionScore.averageScore}
            />
            {benchmarkValue !== undefined
              ? renderGapBarWithTooltip(
                  `${scoreValue}% ${section.name} score`,
                  scoreValue,
                  benchmarkValue,
                )
              : null}
          </div>
        );
      },
    });
  }

  const customerExperienceColumn: EntityTableColumnDefinition<R> | undefined =
    shouldShowCustomerExperienceScoreColumn
      ? {
          id: PerformanceColumnId.CustomerExperienceScore,
          className: "w-0",
          allowsSorting,
          header: headerWithTooltip(
            "CX Score",
            "How the customer felt by the end of the interaction, scored 0–100. Averaged across all interactions in this period.",
          ),
          renderCell: (row) => {
            const cxScore = row.scores.customer_experience_score;

            if (cxScore === undefined) {
              return <span className="text-muted-foreground">--</span>;
            }

            const median = benchmarks.medianCustomerExperienceScore;
            const scoreValue = customerExperienceScoreToPercent(cxScore);
            const benchmarkValue =
              median !== undefined
                ? customerExperienceScoreToPercent(median)
                : undefined;

            return (
              <div className="flex items-center gap-2">
                <CustomerExperienceScorePercentPill
                  size={Size.SMALL}
                  score={cxScore}
                />
                {benchmarkValue !== undefined
                  ? renderGapBarWithTooltip(
                      `${Math.round(scoreValue)} CX score`,
                      scoreValue,
                      benchmarkValue,
                    )
                  : null}
              </div>
            );
          },
        }
      : undefined;

  const pushCustomerExperienceColumn = () => {
    if (customerExperienceColumn) {
      columns.push(customerExperienceColumn);
    }
  };

  // Add outcome column if playbook has an outcome definition
  if (outcomeDefinition) {
    const headerTitle = buildOutcomeGreenRateLabel(outcomeDefinition);
    const outcomeNoun = getOutcomeNoun(outcomeDefinition);
    columns.push({
      id: PerformanceColumnId.OutcomeScore,
      className: "w-0",
      allowsSorting,
      header: headerWithTooltip(
        headerTitle,
        `How often their calls end in a ${outcomeNoun}.`,
      ),
      renderCell: (row) => {
        const outcomeRate = row.scores.outcome_green_rate;

        if (outcomeRate === undefined) {
          return <span className="text-muted-foreground">--</span>;
        }

        const scoreValue = Math.round(outcomeRate * 100);
        const benchmarkValue =
          benchmarks.medianOutcomeGreenRate &&
          Math.round(benchmarks.medianOutcomeGreenRate * 100);

        return (
          <div className="flex items-center gap-2">
            <OutcomeRatePill size={Size.SMALL} outcomeRate={outcomeRate} />
            {isNotNullNotUndefined(benchmarkValue)
              ? renderGapBarWithTooltip(
                  `${scoreValue}% ${headerTitle.toLowerCase()}`,
                  scoreValue,
                  benchmarkValue,
                )
              : null}
          </div>
        );
      },
    });

    const pluralOutcomeNoun = getOutcomePluralNoun(outcomeDefinition);
    const countHeaderTitle = capitalize(pluralOutcomeNoun);
    columns.push({
      id: PerformanceColumnId.OutcomeCount,
      className: "w-0",
      allowsSorting,
      header: headerWithTooltip(
        countHeaderTitle,
        `Total ${pluralOutcomeNoun} in this period.`,
      ),
      renderCell: (row) => {
        const count = row.scores.outcome_green_count;
        if (count === undefined) {
          return <span className="text-muted-foreground">--</span>;
        }

        const benchmark = benchmarks.medianOutcomeGreenCount;

        return (
          <div className="flex items-center gap-2">
            <span>{count.toLocaleString()}</span>
            {benchmark !== undefined
              ? renderGapBarWithTooltip(
                  `${count.toLocaleString()} ${pluralOutcomeNoun}`,
                  count,
                  benchmark,
                  {
                    range: [0, Math.max(1, Math.max(count, benchmark) * 2)],
                    deltaDecimalPlaces: 0,
                    formatDelta: (delta) => delta.toLocaleString(),
                  },
                )
              : null}
          </div>
        );
      },
    });

    pushCustomerExperienceColumn();

    // Avg-time-to-outcome column for non-revenue outcomes. Revenue
    // playbooks already get a more meaningful dollar-denominated per-hour
    // metric, so we omit this column there. The value is the average
    // recorded call duration on each green-final account (each account
    // contributes its full call time once).
    if (!isRevenueOutcomeDefinition(outcomeDefinition)) {
      const noun = getOutcomeNoun(outcomeDefinition);
      const avgTimeHeaderTitle = `Avg time to ${noun}`;

      columns.push({
        id: PerformanceColumnId.SecondsPerGreenOutcome,
        className: "w-0",
        allowsSorting,
        header: headerWithTooltip(
          avgTimeHeaderTitle,
          `Average call time spent on each ${noun}.`,
        ),
        renderCell: (row) => {
          const avgSecondsPerOutcome = row.scores.seconds_per_green_outcome;

          if (avgSecondsPerOutcome === undefined || avgSecondsPerOutcome <= 0) {
            return <span className="text-muted-foreground">--</span>;
          }

          const formattedAvgTime =
            formatCallLengthHoursMinutes(avgSecondsPerOutcome);
          const median = benchmarks.medianSecondsPerGreenOutcome;

          return (
            <div className="flex items-center gap-2">
              <Pill
                color={Color.Gray}
                size={Size.SMALL}
                label={formattedAvgTime}
              />
              {median !== undefined && median > 0
                ? renderGapBarWithTooltip(
                    `${formattedAvgTime} avg time to ${noun}`,
                    avgSecondsPerOutcome,
                    median,
                    {
                      range: [
                        0,
                        Math.max(
                          60,
                          Math.max(avgSecondsPerOutcome, median) * 2,
                        ),
                      ],
                      lowerIsBetter: true,
                      deltaDecimalPlaces: 0,
                      formatDelta: formatCallLengthHoursMinutes,
                    },
                  )
                : null}
            </div>
          );
        },
      });
    }

    // Add avg revenue column for revenue outcomes
    if (isRevenueOutcomeDefinition(outcomeDefinition)) {
      columns.push({
        id: PerformanceColumnId.AvgRevenue,
        className: "w-0",
        allowsSorting,
        header: headerWithTooltip(
          "Premium / Opp",
          "Average premium per opportunity won.",
        ),
        renderCell: (row) => {
          const avgRevenue = row.scores.average_revenue;

          if (avgRevenue === undefined || avgRevenue === null) {
            return <span className="text-muted-foreground">--</span>;
          }

          const benchmark = benchmarks.averageRevenueBenchmark;

          return (
            <div className="flex items-center gap-2">
              <Pill
                color={Color.Gray}
                size={Size.SMALL}
                label={`$${Math.round(avgRevenue).toLocaleString()}`}
              />
              {benchmark !== undefined && benchmark > 0
                ? renderGapBarWithTooltip(
                    `$${Math.round(avgRevenue).toLocaleString()} avg premium`,
                    avgRevenue,
                    benchmark,
                    {
                      benchmarkLabel: "average",
                      range: [0, Math.max(avgRevenue, benchmark) * 2],
                      deltaPrefix: "$",
                      deltaDecimalPlaces: 0,
                    },
                  )
                : null}
            </div>
          );
        },
      });

      columns.push({
        id: PerformanceColumnId.PremiumPerHour,
        className: "w-0",
        allowsSorting,
        header: headerWithTooltip(
          "Premium / hr",
          "Premium earned per hour of call time.",
        ),
        renderCell: (row) => {
          const revenuePerHour = row.scores.revenue_per_hour;

          if (revenuePerHour === undefined) {
            return <span className="text-muted-foreground">--</span>;
          }

          const median = benchmarks.medianRevenuePerHour;

          return (
            <div className="flex items-center gap-2">
              <Pill
                color={Color.Gray}
                size={Size.SMALL}
                label={`$${Math.round(revenuePerHour).toLocaleString()}`}
              />
              {median !== undefined
                ? renderGapBarWithTooltip(
                    `$${Math.round(revenuePerHour).toLocaleString()} premium/hr`,
                    revenuePerHour,
                    median,
                    {
                      range: [
                        0,
                        Math.max(100, Math.max(revenuePerHour, median) * 2),
                      ],
                      deltaPrefix: "$",
                      deltaDecimalPlaces: 0,
                    },
                  )
                : null}
            </div>
          );
        },
      });
    }
  }

  if (!outcomeDefinition) {
    pushCustomerExperienceColumn();
  }

  // Add knowledge base adherence score if the playbook has knowledge bases
  if (shouldShowKnowledgeBaseAdherenceColumn) {
    columns.push({
      id: PerformanceColumnId.KnowledgeBaseAdherenceScore,
      className: "w-0",
      allowsSorting,
      header: headerWithTooltip(
        "KB",
        "How often their answers match your knowledge base.",
      ),
      renderCell: (row) => {
        const incorrectRate = row.scores.incorrect_call_ratio;
        const correctRate =
          incorrectRate !== undefined ? 1 - incorrectRate : undefined;

        return (
          <PercentagePill
            size={Size.SMALL}
            value={correctRate}
            computeColor={computePassRatePillColor}
          />
        );
      },
    });
  }

  columns.push({
    id: PerformanceColumnId.TalkRatio,
    className: "w-0",
    allowsSorting,
    header: headerWithTooltip(
      "Talk ratio",
      "Share of talking time the rep takes on calls.",
    ),
    renderCell: (row) => {
      const talkRatio = row.scores.talk_ratio;

      if (talkRatio === undefined) {
        return <span className="text-muted-foreground">--</span>;
      }

      const scoreValue = Math.round(talkRatio * 100);
      const benchmarkValue =
        benchmarks.medianTalkRatio !== undefined
          ? Math.round(benchmarks.medianTalkRatio * 100)
          : undefined;

      return (
        <div className="flex items-center gap-2">
          <GradingTalkRatioPill size={Size.SMALL} talkRatio={talkRatio} />
          {benchmarkValue !== undefined
            ? renderGapBarWithTooltip(
                `Rep talking ${scoreValue}% of the time`,
                scoreValue,
                benchmarkValue,
                { shouldUseNeutralColor: true },
              )
            : null}
        </div>
      );
    },
  });

  const disjunctionFormatter = new Intl.ListFormat("en-US", {
    style: "short",
    type: "disjunction",
  });

  for (const computedField of computedFields ?? []) {
    const columnId = buildComputedFieldColumnId(computedField.id);
    columns.push({
      id: columnId,
      className: "w-0",
      allowsSorting,
      header: headerWithTooltip(
        <span className="max-w-[120px] truncate">
          {computedField.display_name}
        </span>,
        computedField.computation.type === "rate_in"
          ? `Conversion rate from ${disjunctionFormatter.format(
              computedField.computation.denominator_values,
            )} stage to ${disjunctionFormatter.format(
              computedField.computation.numerator_values,
            )} stage.`
          : `Number of calls in ${disjunctionFormatter.format(
              computedField.computation.values,
            )} stage.`,
      ),
      renderCell: (row) => {
        const value = row.computed_field_values?.[computedField.id];

        if (value === undefined || value === null) {
          return <span className="text-muted-foreground">--</span>;
        }

        const median = benchmarks.medianComputedFieldValues?.[computedField.id];

        if (computedField.computation.type === "rate_in") {
          const scoreValue = Math.round(value * 100);
          const benchmarkValue =
            median !== undefined && median !== null
              ? Math.round(median * 100)
              : undefined;
          return (
            <div className="flex items-center gap-2">
              <PercentagePill size={Size.SMALL} value={value} />
              {benchmarkValue !== undefined
                ? renderGapBarWithTooltip(
                    `${scoreValue}% ${computedField.display_name.toLowerCase()}`,
                    scoreValue,
                    benchmarkValue,
                  )
                : null}
            </div>
          );
        }

        // count_in
        const count = Math.round(value);
        return (
          <div className="flex items-center gap-2">
            <Pill
              color={Color.Gray}
              size={Size.SMALL}
              label={count.toLocaleString()}
            />
            {median !== undefined && median !== null
              ? renderGapBarWithTooltip(
                  `${count.toLocaleString()} ${computedField.display_name.toLowerCase()}`,
                  count,
                  median,
                  {
                    range: [0, Math.max(1, Math.max(count, median) * 2)],
                    deltaDecimalPlaces: 0,
                    formatDelta: (delta) => delta.toLocaleString(),
                  },
                )
              : null}
          </div>
        );
      },
    });
  }

  return columns.filter((column) => {
    if (!isTogglableMetricColumnId(column.id)) {
      return true;
    }
    return selectedMetricColumnIds.has(column.id);
  });
}
